import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { notificationsApi, NotificationAnalyticsResponse, NotificationAnalyticsParams } from '../../api/notifications';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getErrorMessage } from '@/lib/errors';
import { EmptyState, ErrorState, FilterChips, FormField, KpiCard, PageHeader, StatusPill, type FilterChip } from '@/components/app';
import { NOTIFICATION_STATUS_LABELS, NOTIFICATION_TYPE_LABELS, labelOf } from '@/lib/labels';
import { notificationStatusTone } from '@/lib/status';
import { TASHKENT_TZ, tashkentDayEndIso, tashkentDayStartIso } from '@/lib/datetime';
import { useTokenColors } from '@/hooks/useTokenColors';

const fmt = (n: number) => n.toLocaleString('ru-RU');
const fmtDateTime = (iso: string) =>
  new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TASHKENT_TZ }).format(new Date(iso));

const SERIES: Array<{ key: 'sent' | 'delivered' | 'opened'; label: string }> = [
  { key: 'sent', label: 'Отправлено' },
  { key: 'delivered', label: 'Доставлено' },
  { key: 'opened', label: 'Открыто' },
];

interface TooltipEntry { dataKey?: string | number; name?: string; value?: number; color?: string }

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string | number }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-surface px-3.5 py-2.5 text-text shadow-pop">
      {label !== undefined && <p className="mb-1.5 text-[13px] font-semibold">{label}</p>}
      {payload.map((e) => (
        <div key={String(e.dataKey)} className="flex items-center gap-2 text-[13px]">
          <span className="size-2.5 rounded-[3px]" style={{ backgroundColor: e.color }} aria-hidden />
          <span className="text-text-2">{e.name}:</span>
          <span className="font-semibold tabular-nums">{fmt(e.value ?? 0)}</span>
        </div>
      ))}
    </div>
  );
}

function ChartCard({ title, label, legend, children }: { title: string; label: string; legend?: ReactNode; children: ReactNode }) {
  return (
    <Card role="region" aria-label={label} className="flex min-w-0 flex-col gap-3.5 p-[18px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <b className="font-display text-[17px] font-bold">{title}</b>
        {legend}
      </div>
      {children}
    </Card>
  );
}

export default function NotificationAnalyticsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const c = useTokenColors();
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<NotificationAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationAnalyticsParams>({});

  const fetchAnalytics = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      // В фильтрах — дни по Ташкенту; «по» включает выбранный день целиком.
      const data = await notificationsApi.getAnalytics(id, {
        start_date: filters.start_date ? (tashkentDayStartIso(filters.start_date) ?? undefined) : undefined,
        end_date: filters.end_date ? (tashkentDayEndIso(filters.end_date) ?? undefined) : undefined,
      });
      setAnalytics(data);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Не удалось загрузить аналитику'));
    } finally {
      setIsLoading(false);
    }
  }, [id, filters]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const seriesColor: Record<string, string> = { sent: c.text, delivered: c.accent, opened: c.pos };

  const backButton = (
    <Button variant="link" onClick={() => navigate('/notifications')} className="self-start">
      <ArrowLeft aria-hidden />
      Назад
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageHeader title="Аналитика уведомления" />
        <Card>
          <ErrorState message={error} onRetry={fetchAnalytics} />
        </Card>
      </div>
    );
  }

  if (!isLoading && !analytics) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageHeader title="Аналитика уведомления" />
        <Card><EmptyState title="Нет данных аналитики" /></Card>
      </div>
    );
  }

  const notification = analytics?.notification;
  const logs = analytics?.logs_summary;

  const chips: FilterChip[] = [];
  if (filters.start_date) chips.push({ key: 'start', label: `С ${filters.start_date.split('-').reverse().join('.')}`, onRemove: () => setFilters((f) => ({ ...f, start_date: undefined })) });
  if (filters.end_date) chips.push({ key: 'end', label: `По ${filters.end_date.split('-').reverse().join('.')}`, onRemove: () => setFilters((f) => ({ ...f, end_date: undefined })) });

  const kpis = notification
    ? [
        { label: 'Адресовано', value: notification.recipients_count, hint: 'Увидят сообщение в приложении' },
        { label: 'Push отправлен', value: notification.push_sent_count, hint: 'Из них разрешили уведомления' },
        { label: 'Принято Expo', value: notification.accepted_count, hint: 'Попыток по устройствам' },
        { label: 'Доставлено', value: notification.delivered_count, rate: notification.delivery_rate, hint: 'Подтверждено квитанцией' },
        { label: 'Открыли push', value: notification.push_opened_count, rate: notification.push_open_rate, hint: 'Тап по уведомлению' },
        { label: 'Прочитали', value: notification.read_count, rate: notification.read_rate, hint: 'Пушем или в приложении' },
      ]
    : [];

  const statusData = notification
    ? [
        { name: 'Успешно', value: notification.status_breakdown.success },
        { name: 'Ошибка', value: notification.status_breakdown.failed },
        { name: 'Неверный токен', value: notification.status_breakdown.invalid_token },
        { name: 'Не зарегистрирован', value: notification.status_breakdown.device_not_registered },
      ]
    : [];

  return (
    <div className="flex flex-col gap-5">
      {backButton}
      <PageHeader
        title="Аналитика уведомления"
        subtitle={
          notification ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-text">{notification.title}</span>
              <StatusPill size="sm">{labelOf(NOTIFICATION_TYPE_LABELS, notification.type)}</StatusPill>
              <StatusPill size="sm" tone={notificationStatusTone(notification.status)}>
                {labelOf(NOTIFICATION_STATUS_LABELS, notification.status)}
              </StatusPill>
            </span>
          ) : (
            <Skeleton className="h-5 w-64" />
          )
        }
      />

      <Card className="flex flex-col gap-3 p-[18px]">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label="Дата начала" tone="filter" className="w-[180px]">
            <Input type="date" value={filters.start_date || ''} onChange={(e) => setFilters({ ...filters, start_date: e.target.value || undefined })} />
          </FormField>
          <FormField label="Дата конца" tone="filter" className="w-[180px]">
            <Input type="date" value={filters.end_date || ''} onChange={(e) => setFilters({ ...filters, end_date: e.target.value || undefined })} />
          </FormField>
          <FilterChips chips={chips} onReset={() => setFilters({})} className="pb-1.5" />
        </div>
      </Card>

      {/* Сноска для уведомлений, созданных до перехода на модель получателей */}
      {notification?.is_legacy && (
        <div role="note" className="flex items-start gap-2.5 rounded-xl bg-warn-soft px-3.5 py-3 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <div>
            <p className="font-semibold text-warn">Данные до перехода на новую схему</p>
            <p className="mt-1 text-text-2">
              У этой рассылки «адресовано» совпадает с «получили push»: людей без
              разрешения на уведомления тогда не адресовали вовсе. Открытия пушей
              не отслеживались — ноль здесь означает «не измеряли», а не «никто не открыл».
            </p>
          </div>
        </div>
      )}

      {/* Воронка: от адресатов до прочтения */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {isLoading && !notification
          ? Array.from({ length: 6 }, (_, i) => <KpiCard key={i} label=" " value="" loading />)
          : kpis.map((k) => (
              <KpiCard
                key={k.label}
                label={k.label}
                value={fmt(k.value)}
                hint={k.rate !== undefined ? `${k.rate.toFixed(1)}% · ${k.hint}` : k.hint}
                loading={isLoading}
              />
            ))}
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <ChartCard
          title="Хронология"
          label="Хронология доставки по дням"
          legend={
            <div className="flex gap-4 text-[13px] text-text-2">
              {SERIES.map((s) => (
                <span key={s.key} className="flex items-center gap-1.5">
                  <span aria-hidden className="size-2.5 rounded-[3px]" style={{ backgroundColor: seriesColor[s.key] }} />
                  {s.label}
                </span>
              ))}
            </div>
          }
        >
          {isLoading || !logs ? (
            <Skeleton className="h-[280px] w-full" />
          ) : logs.by_date.length === 0 ? (
            <EmptyState title="Нет событий за период" className="h-[280px] py-0" />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={logs.by_date} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={c['border-soft']} vertical={false} />
                  <XAxis dataKey="date" stroke={c.border} tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} />
                  <YAxis tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  {SERIES.map((s) => (
                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={seriesColor[s.key]} strokeWidth={2} dot={false} isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="По статусам доставки" label="Попытки доставки по статусам">
          {isLoading || !notification ? (
            <Skeleton className="h-[280px] w-full" />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid stroke={c['border-soft']} vertical={false} />
                  <XAxis dataKey="name" stroke={c.border} tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} />
                  <YAxis tick={{ fill: c['text-2'], fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: c.muted, opacity: 0.6 }} content={<ChartTooltip />} />
                  <Bar dataKey="value" name="Попыток" fill={c.text} radius={[3, 3, 0, 0]} maxBarSize={48} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>

      <Card role="region" aria-label="Детали" className="flex flex-col gap-3.5 p-[18px]">
        <b className="font-display text-[17px] font-bold">Детали</b>
        {!notification ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <dl className="m-0 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Detail label="Создано" value={fmtDateTime(notification.created_at)} />
            {notification.scheduled_at && <Detail label="Запланировано" value={fmtDateTime(notification.scheduled_at)} />}
            {notification.sent_at && <Detail label="Отправлено" value={fmtDateTime(notification.sent_at)} />}
            {notification.estimated_recipients != null && <Detail label="Ожидаемых получателей" value={fmt(notification.estimated_recipients)} />}
          </dl>
        )}
        <span className="text-xs text-text-2">Время — по Ташкенту</span>
      </Card>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[13px] text-text-2">{label}</dt>
      <dd className="m-0 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
